import { useSuspenseQuery } from '@tanstack/react-query';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { Page } from '@/app/Page';
import {
  ActionLogEmpty,
  ActionTimeline,
  actionLogsQueryOptions,
  searchActionLogEntries,
} from '@/features/action-log';
import { clientsQueryOptions } from '@/features/clients';
import type { ClientHistoryLocationState } from '@/navigation/paths';
import { useSearchBarState, useTelegramBackButton } from '@/shared/hooks';
import { QueryBoundary, QueryErrorPlaceholder, SearchBar } from '@/shared/ui';
import { TEST_IDS } from '@/testIds';
import { ClientSubPageLayout } from './client-profile-page/ClientSubPageLayout';
import styles from './ClientHistoryPage.module.css';

export const ClientHistoryPage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>();

  if (!clientId) {
    return (
      <Page back={true}>
        <QueryErrorPlaceholder header="Not found" />
      </Page>
    );
  }

  return (
    <Page back={true}>
      <QueryBoundary resetKeys={[clientId]}>
        <ClientHistoryPageContent clientId={clientId} />
      </QueryBoundary>
    </Page>
  );
};

interface ClientHistoryPageContentProps {
  clientId: string;
}

const ClientHistoryPageContent: FC<ClientHistoryPageContentProps> = ({ clientId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: client } = useSuspenseQuery(clientsQueryOptions.detail(clientId));
  const { data: history } = useSuspenseQuery(actionLogsQueryOptions.byClient(clientId));
  const {
    searchQuery,
    setSearchQuery,
    isSearchVisible,
    openSearch,
    closeSearch,
    searchTriggerRef,
  } = useSearchBarState();
  const normalizedState = (location.state as ClientHistoryLocationState | null) ?? null;
  const shouldFocusSearch = normalizedState?.focusSearch === true;
  const hasHistory = history.items.length > 0;
  const filteredHistory = useMemo(
    () => searchActionLogEntries(history.items, searchQuery),
    [history.items, searchQuery]
  );

  useEffect(() => {
    if (!shouldFocusSearch) return;

    if (hasHistory) {
      openSearch();
    }

    void navigate('.', { replace: true, state: {} });
  }, [hasHistory, navigate, openSearch, shouldFocusSearch]);

  useTelegramBackButton({ visible: isSearchVisible, onBack: closeSearch });

  const searchBar = hasHistory ? (
    <SearchBar
      triggerRef={searchTriggerRef}
      isVisible={isSearchVisible}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onClose={closeSearch}
      onOpen={openSearch}
    />
  ) : null;

  return (
    <ClientSubPageLayout
      title="History"
      subtitle={client.name}
      titleTestId={TEST_IDS.historyPageTitle}
      action={searchBar}
      activeHeader={searchBar}
      isHeaderActive={isSearchVisible}
    >
      {history.items.length === 0 ? (
        <div data-testid={TEST_IDS.historyPageEmpty}>
          <ActionLogEmpty />
        </div>
      ) : (
        <div className={styles.body}>
          <ActionTimeline entries={filteredHistory} emptyHint="No matching actions" />
        </div>
      )}
    </ClientSubPageLayout>
  );
};
